/** Firmwell V5 Clean Light theme interactions. */
class FirmwellDialog extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    this.returnFocus = null;
    this.triggers = document.querySelectorAll(`[data-dialog-open="${this.dialog.id}"]`);
    this.triggers.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(button);
      });
    });
    this.querySelectorAll('[data-dialog-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('dialog-open');
      this.dialog.classList.remove('is-open');
      this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      this.returnFocus?.focus();
    });
  }

  open(trigger) {
    document.querySelectorAll('dialog[open]').forEach((openDialog) => {
      if (openDialog !== this.dialog) openDialog.close();
    });
    this.returnFocus = trigger || document.activeElement;
    this.dialog.showModal();
    document.documentElement.classList.add('dialog-open');
    this.triggers.forEach((item) => item.setAttribute('aria-expanded', 'true'));
    requestAnimationFrame(() => this.dialog.classList.add('is-open'));
    this.dialog.querySelector('input:not([type="hidden"]), [data-dialog-close], a, button')?.focus();
  }

  close() {
    if (!this.dialog.open) return;
    if (!this.dialog.hasAttribute('data-animated-close') || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.dialog.close();
      return;
    }

    this.dialog.classList.remove('is-open');
    let closeTimer;
    const finishClose = () => {
      window.clearTimeout(closeTimer);
      this.dialog.removeEventListener('transitionend', handleTransitionEnd);
      if (this.dialog.open) this.dialog.close();
    };
    const handleTransitionEnd = (event) => {
      if (event.target === this.dialog && event.propertyName === 'transform') finishClose();
    };
    this.dialog.addEventListener('transitionend', handleTransitionEnd);
    closeTimer = window.setTimeout(finishClose, 500);
  }
}

class SearchDrawer extends FirmwellDialog {
  connectedCallback() {
    super.connectedCallback();
    this.form = this.querySelector('[data-search-form]');
    this.input = this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-search-results]');
    this.status = this.querySelector('[data-search-status]');
    this.clearButton = this.querySelector('[data-search-clear]');
    this.controller = null;
    this.timer = null;
    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.search(this.input.value);
    });
    this.input?.addEventListener('input', () => {
      window.clearTimeout(this.timer);
      this.clearButton.hidden = this.input.value.length === 0;
      this.timer = window.setTimeout(() => this.search(this.input.value), 180);
    });
    this.clearButton?.addEventListener('click', () => {
      this.input.value = '';
      this.clearButton.hidden = true;
      this.results.replaceChildren();
      this.status.textContent = this.input.placeholder;
      this.input.focus();
    });
  }

  open(trigger) {
    super.open(trigger);
    requestAnimationFrame(() => this.input?.focus());
  }

  escape(value) {
    const element = document.createElement('span');
    element.textContent = String(value || '');
    return element.innerHTML;
  }

  async search(rawQuery) {
    const query = rawQuery.trim();
    if (query.length < 2) {
      this.controller?.abort();
      this.results.replaceChildren();
      this.status.textContent = query.length ? window.Firmwell.strings.searchEmpty : this.input.placeholder;
      return;
    }
    this.controller?.abort();
    this.controller = new AbortController();
    this.status.textContent = window.Firmwell.strings.searchLoading;
    this.results.setAttribute('aria-busy', 'true');
    try {
      const url = new URL(`${window.Shopify.routes.root}search/suggest.json`, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,article,page');
      url.searchParams.set('resources[limit]', '6');
      const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: this.controller.signal });
      if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
      const data = await response.json();
      this.render(data.resources?.results || {});
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.results.replaceChildren();
        this.status.textContent = window.Firmwell.strings.searchEmpty;
      }
    } finally {
      this.results.removeAttribute('aria-busy');
    }
  }

  render(groups) {
    const items = [
      ...(groups.products || []).map((item) => ({ ...item, resultType: 'Product' })),
      ...(groups.articles || []).map((item) => ({ ...item, resultType: 'Article' })),
      ...(groups.pages || []).map((item) => ({ ...item, resultType: 'Page' }))
    ];
    if (!items.length) {
      this.results.replaceChildren();
      this.status.textContent = window.Firmwell.strings.searchEmpty;
      return;
    }
    this.results.innerHTML = items.map((item) => {
      const imageValue = typeof item.image === 'string' ? item.image : item.image?.url;
      const image = imageValue ? `<img src="${this.escape(imageValue)}&width=180" alt="" width="90" height="112">` : '';
      return `<a class="search-result-card" href="${this.escape(item.url)}">${image}<span><small>${this.escape(item.resultType)}</small><strong>${this.escape(item.title)}</strong></span></a>`;
    }).join('');
    this.status.textContent = window.Firmwell.strings.searchResults.replace('[count]', String(items.length));
  }
}

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="number"]');
    this.querySelector('[data-quantity-minus]')?.addEventListener('click', () => this.change(-1));
    this.querySelector('[data-quantity-plus]')?.addEventListener('click', () => this.change(1));
  }

  change(step) {
    const min = Number(this.input.min || 1);
    this.input.value = Math.max(min, Number(this.input.value || min) + step);
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.variants = JSON.parse(this.querySelector('[data-product-json]')?.textContent || '[]');
    this.form?.addEventListener('submit', (event) => this.submit(event));
    this.querySelectorAll('input[name^="options["]').forEach((input) => input.addEventListener('change', () => this.selectVariant()));
    this.querySelectorAll('[data-purchase-option]').forEach((input) => input.addEventListener('change', () => this.selectPurchase(input)));
  }

  formatMoney(cents) {
    const amount = (Number(cents) / 100).toFixed(2);
    return window.Firmwell.formatMoney
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', String(Math.round(Number(cents) / 100)));
  }

  selectVariant() {
    const selected = [...this.querySelectorAll('fieldset[data-option-index]')].map((field) => field.querySelector('input:checked')?.value);
    const variant = this.variants.find((item) => item.options.every((option, index) => option === selected[index]));
    if (!variant) return;
    this.querySelector('[data-variant-id]').value = variant.id;
    const button = this.querySelector('[data-add-to-cart]');
    button.disabled = !variant.available;
    this.querySelector('[data-add-label]').textContent = variant.available ? window.Firmwell.strings.addToCart : window.Firmwell.strings.soldOut;
    document.querySelectorAll('[data-product-price]').forEach((price) => { price.textContent = this.formatMoney(variant.price); });
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  selectPurchase(input) {
    const planInput = this.querySelector('[data-selling-plan]');
    if (planInput) planInput.value = input.value === 'subscribe' ? input.dataset.sellingPlanId : '';
  }

  async submit(event) {
    event.preventDefault();
    const button = this.querySelector('[data-add-to-cart], button[type="submit"]');
    const label = button.querySelector('[data-add-label]');
    const error = this.querySelector('[data-product-error]');
    const drawer = document.querySelector('cart-drawer');
    const drawerWasOpen = Boolean(drawer?.dialog?.open);
    const liveRegion = document.querySelector('#FirmwellLiveRegion');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (label) label.textContent = window.Firmwell.strings.adding;
    if (error) error.hidden = true;
    if (!drawerWasOpen) drawer?.open(button);
    drawer?.setLoading(true);
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(this.form)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || window.Firmwell.strings.cartError);
      await drawer?.refresh();
      if (label) label.textContent = window.Firmwell.strings.added;
      if (liveRegion) {
        liveRegion.textContent = window.Firmwell.strings.itemAdded.replace('[title]', result.product_title || '');
      }
      window.setTimeout(() => {
        if (label?.textContent === window.Firmwell.strings.added) {
          label.textContent = window.Firmwell.strings.addToCart;
        }
      }, 1200);
    } catch (exception) {
      if (!drawerWasOpen) drawer?.close();
      if (error) {
        error.textContent = exception.message;
        error.hidden = false;
      }
      if (liveRegion) liveRegion.textContent = exception.message;
      if (label) label.textContent = window.Firmwell.strings.addToCart;
    } finally {
      drawer?.setLoading(false);
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

class CartDrawer extends FirmwellDialog {
  connectedCallback() {
    super.connectedCallback();
    this.content = this.querySelector('[data-cart-items]');
    document.addEventListener('firmwell:cart-refresh', () => this.refresh());
    this.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-cart-remove]');
      if (remove) this.updateLine(remove.dataset.cartRemove, 0);
    });
    this.addEventListener('change', (event) => {
      const input = event.target.closest('[data-cart-quantity]');
      if (input) this.updateLine(input.dataset.cartQuantity, Number(input.value));
    });
  }

  async getCart() {
    const response = await fetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } });
    return response.json();
  }

  async refresh() {
    const cart = await this.getCart();
    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = cart.item_count;
      node.hidden = cart.item_count === 0;
    });
    this.render(cart);
  }

  setLoading(isLoading) {
    this.dialog?.classList.toggle('is-loading', isLoading);
    this.content?.toggleAttribute('aria-busy', isLoading);
  }

  async updateLine(line, quantity) {
    this.setLoading(true);
    try {
      await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity })
      });
      await this.refresh();
    } finally {
      this.setLoading(false);
    }
  }

  render(cart) {
    if (!this.content) return;
    const money = (cents) => window.Firmwell.formatMoney
      .replace('{{amount}}', (cents / 100).toFixed(2))
      .replace('{{amount_no_decimals}}', String(Math.round(cents / 100)));
    if (!cart.items.length) {
      this.content.innerHTML = `<div class="cart-drawer__empty"><p>${window.Firmwell.strings.emptyCart}</p><a class="button button--primary" href="${window.Shopify.routes.root}collections/all">${window.Firmwell.strings.continueShopping}</a></div>`;
    } else {
      this.content.innerHTML = cart.items.map((item, index) => `<article class="cart-drawer__item">
        <a href="${item.url}" tabindex="-1"><img src="${item.image ? item.image + '&width=240' : window.Firmwell.placeholder}" alt="" width="120" height="150"></a>
        <div><p class="micro-label">${item.product_type || ''}</p><a class="cart-drawer__item-title" href="${item.url}">${item.product_title}</a><p>${money(item.final_line_price)}</p>
        <div class="cart-drawer__item-actions"><input aria-label="${window.Firmwell.strings.quantity}" type="number" min="0" value="${item.quantity}" data-cart-quantity="${index + 1}"><button class="text-link" type="button" data-cart-remove="${index + 1}">${window.Firmwell.strings.remove}</button></div></div>
      </article>`).join('');
    }
    this.querySelector('[data-cart-subtotal]').textContent = money(cart.total_price);
    const threshold = Number(this.dataset.shippingThreshold || 0);
    const progress = threshold ? Math.min(100, cart.total_price / threshold * 100) : 100;
    this.querySelector('[data-shipping-progress]')?.style.setProperty('--shipping-progress', `${progress}%`);
    const shippingText = this.querySelector('[data-shipping-text]');
    if (shippingText) shippingText.textContent = threshold && cart.total_price < threshold
      ? window.Firmwell.strings.shippingRemaining.replace('[amount]', money(threshold - cart.total_price))
      : window.Firmwell.strings.shippingUnlocked;
  }
}

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-gallery-thumb]').forEach((button) => button.addEventListener('click', () => {
      this.querySelectorAll('[data-gallery-slide]').forEach((slide) => { slide.hidden = slide.dataset.mediaId !== button.dataset.galleryThumb; });
      this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => thumb.setAttribute('aria-current', String(thumb === button)));
    }));
  }
}

class StickyAddToCart extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-sticky-add]');
    this.label = this.querySelector('[data-sticky-add-label]');
    this.form = document.querySelector(`#ProductForm-${CSS.escape(this.dataset.sectionId)}`);
    this.mainButton = this.form?.querySelector('[data-add-to-cart]');
    if (!this.button || !this.mainButton) return;

    this.button.addEventListener('click', () => this.mainButton.click());
    this.stateObserver = new MutationObserver(() => this.syncState());
    this.stateObserver.observe(this.mainButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-busy', 'class'],
      childList: true,
      subtree: true,
      characterData: true
    });
    this.visibilityObserver = new IntersectionObserver(([entry]) => {
      const isVisible = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      this.classList.toggle('is-visible', isVisible);
      this.setAttribute('aria-hidden', String(!isVisible));
      document.body.classList.toggle('has-mobile-sticky-atc', isVisible);
    });
    this.visibilityObserver.observe(this.mainButton);
    this.syncState();
  }

  disconnectedCallback() {
    this.stateObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    document.body.classList.remove('has-mobile-sticky-atc');
  }

  syncState() {
    const mainLabel = this.mainButton.querySelector('[data-add-label]');
    if (mainLabel && this.label) this.label.textContent = mainLabel.textContent;
    this.button.disabled = this.mainButton.disabled;
    this.button.toggleAttribute('aria-busy', this.mainButton.hasAttribute('aria-busy'));
    this.button.classList.toggle('is-added', this.mainButton.classList.contains('is-added'));
  }
}

class AutoSubmit extends HTMLElement {
  connectedCallback() {
    const form = this.querySelector('form');
    form?.addEventListener('change', () => form.requestSubmit());
  }
}

class RoutineBuilder extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-routine-goal]').forEach((button) => button.addEventListener('click', () => {
      this.querySelectorAll('[data-routine-goal]').forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
      const output = this.querySelector('[data-routine-output]');
      if (output) output.textContent = button.dataset.routineCopy || '';
    }));
  }
}

customElements.define('firmwell-dialog', FirmwellDialog);
customElements.define('search-drawer', SearchDrawer);
customElements.define('quantity-input', QuantityInput);
customElements.define('product-form', ProductForm);
customElements.define('cart-drawer', CartDrawer);
customElements.define('product-gallery', ProductGallery);
customElements.define('sticky-add-to-cart', StickyAddToCart);
customElements.define('auto-submit', AutoSubmit);
customElements.define('routine-builder', RoutineBuilder);

document.addEventListener('DOMContentLoaded', () => document.querySelector('cart-drawer')?.refresh());
